<template>
  <section
    :class="loading == true ? 'h-screen' : 'h-auto'"
    class="container mx-auto px-4 pb-6"
    id="leaderboard"
  >
    <div class="flex items-center justify-center mt-20 max-w-[1400px] mx-auto">
      <div class="flex flex-row gap-2 w-full max-w-[560px]">
        <button
          v-motion-pop-visible
          :class="[
            'px-4 py-2 rounded-md font-medium font-montserrat flex items-center gap-2 justify-center w-full',
            activeTab === 'active'
              ? 'bg-[#1294F0] text-white'
              : 'bg-[#1D1D1D] text-[#A3B3C2]',
          ]"
          @click="
            activeTab = 'active';
            changeTab();
          "
        >
          Active
        </button>
        <button
          v-motion-pop-visible
          :class="[
            'px-4 py-2 rounded-md font-medium font-montserrat flex items-center gap-2 justify-center w-full',
            activeTab === 'completed'
              ? 'bg-[#1294F0] text-white'
              : 'bg-[#1D1D1D] text-[#A3B3C2]',
          ]"
          @click="
            activeTab = 'completed';
            changeTab();
          "
        >
          Completed
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center mt-14">
      <div
        class="w-24 h-24 rounded-full animate-spin border-y-8 border-solid border-white border-t-transparent"
      ></div>
    </div>

    <div
      v-if="!loading && currentCards.length == 0"
      class="h-screen flex justify-center mt-14"
    >
      <div class="text-center text-xl text-white font-bold py-4 uppercase">
        No Challenges to Show
      </div>
    </div>

    <!-- Cards -->
    <div
      v-if="currentCards.length > 0"
      class="flex flex-wrap gap-4 mt-8 max-w-[1400px] mx-auto pb-6 justify-center"
    >
      <div
        v-motion-pop-visible
        v-for="(card, index) in currentCards"
        :key="index"
        class="bg-[#262626] rounded-xl overflow-hidden transition-all duration-300 w-full sm:max-w-[260px] flex flex-col justify-between min-h-[460px]"
      >
        <!-- Card Image -->
        <div class="relative rounded-xl px-4 pt-4">
          <img
            :src="card.imageUrl"
            class="w-full h-[180px] object-cover aspect-square rounded-xl"
            alt=""
          />
          <div
            v-if="card.status == 'completed'"
            class="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center"
          >
            <div class="bg-[#1294F0] rounded-full px-4 py-2 flex items-center gap-2">
              <img src="../assets/trophy.png" class="w-5 h-5" alt="trophy" />
              <span class="text-white text-sm font-medium">{{ card.completedBy }}</span>
            </div>
          </div>
        </div>

        <!-- Card Content -->
        <div class="flex flex-col justify-between flex-grow px-6 py-4">
          <div>
            <h3
              class="text-white text-center font-bold text-md mb-4 uppercase line-clamp-2 h-[50px]"
              :title="card.title"
            >
              {{ card.title }} 
            </h3>

            <!-- Details block -->
            <div class="text-sm space-y-2">
              <div class="space-y-4">
                <div class="flex">
                  <div
                    class="w-1/2 justify-center text-right text-white/70 font-montserrat"
                  >
                    Multiplier :
                  </div>
                  <div
                    class="w-1/2 text-left pl-2 truncate overflow-hidden whitespace-nowrap"
                    :title="card.description"
                  >
                    {{ card.description }}
                  </div>
                </div>
              </div>

              <div class="space-y-4">
                <div class="flex">
                  <div
                    class="w-1/2 justify-center text-right text-white/70 font-montserrat"
                  >
                    Minimum Bet :
                  </div>
                  <div
                    class="w-1/2 text-left pl-2 font-orbitron truncate overflow-hidden whitespace-nowrap"
                    :title="card.minimumBet"
                  >
                    $ {{ card.minimumBet }}
                  </div>
                </div>
              </div>

              <div class="space-y-4">
                <div class="flex">
                  <div
                    class="w-1/2 justify-center text-right text-white/70 font-montserrat"
                  >
                    Reward :
                  </div>
                  <div
                    class="w-1/2 text-left text-[#EEAF0E] font-semibold font-orbitron pl-2 truncate overflow-hidden whitespace-nowrap"
                    :title="card.reward"
                  >
                    $ {{ card.reward }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Action Button -->
          <div class="mt-6 px-4 pb-2">
            <a
              v-if="card.status == 'active'"
              :href="card.challengeUrl"
              class="block w-full bg-[#1294F0] hover:bg-[#1294F0] text-white font-bold py-3 px-6 rounded-md text-center transition-colors duration-300"
            >
              Play Now
            </a>
            <div
              v-if="card.status == 'completed'"
              class="w-full bg-[#1294F0] text-white font-bold py-3 px-6 rounded-md text-center uppercase cursor-default"
            >
              Completed
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import axios from "axios";
import { defineComponent } from "vue";

export default defineComponent({
  data() {
    return {
      loading: false,
      activeTab: "active",
      currentCards: [],
      activeCards: [],
      completedCards: [],
    };
  },
  methods: {
    async init() {
      const baseUrl = process.env.VUE_APP_BASE_API;
      this.loading = true;

      await axios
        .get(`${baseUrl}/api/challenge?status=active&type=metaspins`, {
          headers: { "x-api-key": process.env.VUE_APP_X_API_KEY },
        })
        .then((res) => {
          this.activeCards = res.data.challenges;
        });

      await axios
        .get(`${baseUrl}/api/challenge?status=completed&type=metaspins`, {
          headers: { "x-api-key": process.env.VUE_APP_X_API_KEY },
        })
        .then((res) => {
          this.completedCards = res.data.challenges;
        });

      this.changeTab();
      this.loading = false;
    },
    changeTab() {
      if (this.activeTab == "active") {
        this.currentCards = this.activeCards;
      } else {
        this.currentCards = this.completedCards;
      }
    },
  },
  mounted() {
    this.init();
  },
});
</script>

<style scoped>
.card {
  transition: all 0.3s ease;
}
</style>
